import { FormEvent, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useAuth, LoginStepOneResult } from '@/lib/auth';

type Step = 'credentials' | 'totp';

export function LoginPage() {
  const navigate = useNavigate();
  const { loginPassword, verifyTotp } = useAuth();

  const [step, setStep] = useState<Step>('credentials');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [challenge, setChallenge] = useState<LoginStepOneResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function submitCredentials(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const result = await loginPassword(email, password);
      setChallenge(result);
      setStep('totp');
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? 'Login falhou. Verifique e-mail e senha.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  async function submitTotp(e: FormEvent) {
    e.preventDefault();
    if (!challenge) return;
    setError(null);
    setLoading(true);
    try {
      await verifyTotp(email, code, challenge.challenge_token);
      void navigate({ to: '/' });
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? 'Código TOTP inválido.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex h-full w-full items-center justify-center bg-slate-950 p-6">
      <div className="w-full max-w-sm rounded-xl border border-slate-800 bg-slate-900/70 p-6 shadow-xl">
        <div className="mb-6 text-center">
          <h1 className="text-xl font-semibold">
            JusQuant <span className="text-blue-400">Admin</span>
          </h1>
          <p className="text-xs text-slate-400">Phase 0 — restricted access</p>
        </div>

        {error && (
          <div
            className="mb-4 rounded-md border border-red-900/50 bg-red-950/30 px-3 py-2 text-sm text-red-300"
            role="alert"
          >
            {error}
          </div>
        )}

        {step === 'credentials' && (
          <form onSubmit={submitCredentials} className="space-y-4">
            <label className="block text-sm">
              <span className="mb-1 block text-slate-300">E-mail</span>
              <input
                type="email"
                required
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="block w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-slate-100 focus:border-blue-500 focus:outline-none"
              />
            </label>
            <label className="block text-sm">
              <span className="mb-1 block text-slate-300">Senha</span>
              <input
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="block w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-slate-100 focus:border-blue-500 focus:outline-none"
              />
            </label>
            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-500 disabled:opacity-60"
            >
              {loading ? 'Verificando...' : 'Entrar'}
            </button>
          </form>
        )}

        {step === 'totp' && challenge && (
          <form onSubmit={submitTotp} className="space-y-4">
            {challenge.status === 'enrollment_required' && (
              <div className="space-y-2 rounded-md border border-blue-900/40 bg-blue-950/30 p-3 text-sm text-blue-200">
                <p>
                  Primeiro acesso detectado. Escaneie o QR no seu autenticador
                  (Google Authenticator, 1Password, Authy):
                </p>
                {challenge.qr_png_base64 && (
                  <img
                    src={`data:image/png;base64,${challenge.qr_png_base64}`}
                    alt="QR code TOTP"
                    className="mx-auto h-40 w-40 rounded bg-white p-1"
                  />
                )}
                {challenge.otpauth_uri && (
                  <details className="text-xs text-blue-300">
                    <summary>Secret manual</summary>
                    <code className="break-all">{challenge.otpauth_uri}</code>
                  </details>
                )}
              </div>
            )}

            <label className="block text-sm">
              <span className="mb-1 block text-slate-300">Código (6 dígitos)</span>
              <input
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                required
                autoFocus
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                className="block w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-center font-mono text-lg tracking-widest text-slate-100 focus:border-blue-500 focus:outline-none"
              />
            </label>
            <button
              type="submit"
              disabled={loading || code.length !== 6}
              className="w-full rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-500 disabled:opacity-60"
            >
              {loading ? 'Verificando...' : 'Verificar e entrar'}
            </button>
            <button
              type="button"
              onClick={() => {
                setStep('credentials');
                setChallenge(null);
                setCode('');
              }}
              className="w-full text-xs text-slate-400 hover:text-slate-200"
            >
              ← Voltar
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
